import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CollectionPage from '~/components/module/setup/CollectionPage';
import { str } from '~/components/module/setup/cells';
import { MEMORY_ACCEPT, MemoryType, createMemoryAdapter } from '~/apis/memory';
import { useFilePreview } from '~/utils/hooks/useFilePreview';
import { FormValues, IFormField } from '~/utils/interface/setup';

const TABS: { type: MemoryType; labelKey: string }[] = [
  { type: 'PHOTO', labelKey: 'pages.setup.memories.photos' },
  { type: 'AUDIO', labelKey: 'pages.setup.memories.songs' },
];

const adapters = { PHOTO: createMemoryAdapter('PHOTO'), AUDIO: createMemoryAdapter('AUDIO') };

const formatSize = (bytes: FormValues[string]) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  return value < 1024 * 1024 ? `${(value / 1024).toFixed(0)} KB` : `${(value / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso: FormValues[string]) => {
  const date = new Date(str(iso));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

// Thumbnail for an image, a player for audio, a link otherwise; a just-picked file previews locally.
const MemoryPreview = ({ form, type }: { form: FormValues; type: MemoryType }) => {
  const { t } = useTranslation();
  const src = useFilePreview(form.file);
  const mime = form.file instanceof File ? form.file.type : str(form.mimetype);
  const thumb = typeof form.thumbnail === 'string' && form.thumbnail ? form.thumbnail : undefined;

  if (!src) return null;
  if (mime.startsWith('image/')) {
    return <img src={thumb ?? src} alt='' className='h-12 w-12 rounded-md border border-border object-cover' />;
  }
  if (type === 'AUDIO' && mime.startsWith('audio/')) {
    return <audio controls preload='none' src={src} className='h-8 max-w-[14rem]' />;
  }
  return (
    <a href={src} target='_blank' rel='noreferrer' className='underline'>
      {t('pages.setup.memories.open')}
    </a>
  );
};

const MemoryList = ({ type }: { type: MemoryType }) => {
  const fields: IFormField[] = [
    {
      name: 'file',
      labelKey: 'pages.setup.memories.file',
      type: 'file',
      required: true,
      accept: MEMORY_ACCEPT[type],
      hideOnEdit: true,
    },
    { name: 'description', labelKey: 'pages.setup.memories.description', type: 'textarea' },
  ];

  return (
    <CollectionPage
      i18nPrefix={`pages.setup.memories.${type === 'PHOTO' ? 'photoList' : 'songList'}`}
      module='photo'
      // the app lets whoever can add memories also edit their description
      actionMap={{ update: 'create' }}
      adapter={adapters[type]}
      fields={fields}
      defaultForm={{}}
      getTitle={(form) => str(form.description) || str(form.fileName)}
      columns={[
        { key: 'preview', labelKey: 'pages.setup.memories.file', render: (form) => <MemoryPreview form={form} type={type} /> },
        { key: 'description', labelKey: 'pages.setup.memories.description', render: (form) => <span className='font-medium text-foreground'>{str(form.description)}</span> },
        { key: 'name', labelKey: 'pages.setup.memories.fileName', render: (form) => (form.file instanceof File ? form.file.name : str(form.fileName)) },
        { key: 'size', labelKey: 'pages.setup.memories.size', render: (form) => formatSize(form.file instanceof File ? form.file.size : form.size) },
        { key: 'createdAt', labelKey: 'pages.setup.memories.created', render: (form) => formatDate(form.createdAt) },
      ]}
    />
  );
};

const Memories = () => {
  const { t } = useTranslation();
  const [type, setType] = useState<MemoryType>('PHOTO');

  return (
    <div className='flex w-full flex-col gap-4'>
      <div role='tablist' aria-label={t('pages.setup.memories.title')} className='flex gap-2 border-b border-border'>
        {TABS.map((tab) => (
          <button
            key={tab.type}
            type='button'
            role='tab'
            aria-selected={type === tab.type}
            onClick={() => setType(tab.type)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              type === tab.type
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>
      {/* keyed so switching tabs starts a fresh list (and drops the other tab's unsynced changes' view) */}
      <MemoryList key={type} type={type} />
    </div>
  );
};

export default Memories;
